/**
 * Breadcrumbs Composable
 *
 * Generates breadcrumb items from current route.
 * Supports both plain-string and function-getter labels
 * so that i18n-aware routers can use `() => i18next.t(key)`.
 *
 * The computed depends on the reactive `locale` ref from `useLocale()`,
 * ensuring breadcrumbs re-render immediately when the user switches language.
 */
import { computed, type Component } from 'vue';
import { useRoute } from 'vue-router';
import { useLocale } from '@/shared/composables/useLocale';
import { useSettingsStore } from '@/stores/settings.store';

export interface BreadcrumbItem {
  label: string | (() => string);
  to?: string;
  icon?: Component;
}

/** Resolve a label that may be a plain string or a getter function. */
function resolveLabel(label: string | (() => string)): string {
  return typeof label === 'function' ? label() : label;
}

export function useBreadcrumbs() {
  const route = useRoute();
  const { t, locale } = useLocale();
  const settingsStore = useSettingsStore();

  const breadcrumbs = computed<{ label: string; to?: string; icon?: Component }[]>(() => {
    // Consuming locale.value creates a genuine reactive dependency so the
    // computed re-evaluates when the user switches language. The t() calls
    // and resolveLabel() getters below use the current locale implicitly;
    // referencing it here via t() with the active lng makes the link explicit.
    const homeLabel = t('common.home', { lng: locale.value });

    const items: { label: string; to?: string; icon?: Component }[] = [];

    // Always add home
    items.push({
      label: homeLabel,
      to: '/',
    });

    // Get breadcrumb from route meta or generate from path
    if (route.meta.breadcrumbs) {
      const metaCrumbs = route.meta.breadcrumbs as BreadcrumbItem[];
      items.push(...metaCrumbs.map((b) => ({
        ...b,
        label: resolveLabel(b.label),
      })));
    } else {
      // Auto-generate from path
      const pathSegments = route.path.split('/').filter(Boolean);

      pathSegments.forEach((segment, index) => {
        const path = '/' + pathSegments.slice(0, index + 1).join('/');
        const isLast = index === pathSegments.length - 1;
        const metaTitle = route.meta.title;
        const label = isLast && metaTitle
          ? resolveLabel(metaTitle as string | (() => string))
          : segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

        items.push({
          label,
          to: path,
        });
      });
    }

    // Remove last item's link (current page)
    if (items.length > 0) {
      delete items[items.length - 1].to;
    }

    // In wizard mode (first-time setup), hide the Settings Dashboard crumb so
    // the user cannot click back to the dashboard while the stepper is active.
    const filtered = settingsStore.settingsOnboarded === false
      ? items.filter(item => item.to !== '/app-settings/dashboard')
      : items;

    return filtered;
  });

  return {
    breadcrumbs,
  };
}
